import { FiCheck, FiX } from 'react-icons/fi';

import type { ABTestSessionResponse, ABTestVariantResult } from '../../services/abTestApi';

// ─── Props ─────────────────────────────────────────

interface ABTestResultsTableProps {
  session: ABTestSessionResponse;
  onApply: (variantId: string) => void;
  onClose: () => void;
  isApplying: boolean;
}

// ─── Helpers ───────────────────────────────────────

function formatScore(value: number | undefined): string {
  if (value === undefined || value === null) return '—';
  return `${(value * 100).toFixed(0)}%`;
}

function formatLatency(ms: number | undefined): string {
  if (ms === undefined || ms === null) return '—';
  if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.round(ms)}ms`;
}

function formatCost(cost: number | undefined): string {
  if (cost === undefined || cost === null) return '—';
  return `${cost} cr`;
}

function scoreColor(value: number | undefined): React.CSSProperties {
  if (value === undefined || value === null) return {};
  if (value >= 0.85) return { backgroundColor: '#dcfce7', color: '#166534' };
  if (value >= 0.70) return { backgroundColor: '#fef9c3', color: '#854d0e' };
  return { backgroundColor: '#fee2e2', color: '#991b1b' };
}

function positionBadgeStyle(position: number): React.CSSProperties {
  const base: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    height: 24,
    borderRadius: '50%',
    fontSize: 12,
    fontWeight: 700,
  };

  if (position === 1) return { ...base, backgroundColor: '#fef3c7', color: '#f59e0b' };
  if (position === 2) return { ...base, backgroundColor: '#f1f5f9', color: '#94a3b8' };
  if (position === 3) return { ...base, backgroundColor: '#fef3c7', color: '#d97706' };
  return { ...base, backgroundColor: '#e2e8f0', color: '#64748b' };
}

function rowBackground(position: number): string | undefined {
  if (position === 1) return '#fffbeb';
  return undefined;
}

// ─── Metric keys ───────────────────────────────────

const METRIC_KEYS = [
  { key: 'agentGoalAccuracy', altKey: 'agent_goal_accuracy', label: 'Goal' },
  { key: 'actionCompleteness', altKey: 'action_completeness', label: 'Ação' },
  { key: 'faithfulness', label: 'Ground.' },
  { key: 'hallucination', label: 'Aluc.' },
  { key: 'toolUsage', altKey: 'tool_usage', label: 'Tools' },
  { key: 'answerCompleteness', altKey: 'answer_completeness', label: 'Compl.' },
  { key: 'relevance', label: 'Relev.' },
  { key: 'tone', label: 'Tom' },
] as const;

function getMetric(averages: Record<string, number> | undefined, key: string, altKey?: string): number | undefined {
  if (!averages) return undefined;
  if (averages[key] !== undefined) return averages[key];
  if (altKey && averages[altKey] !== undefined) return averages[altKey];
  return undefined;
}

// ─── Sort variants ─────────────────────────────────

function getSortedVariants(session: ABTestSessionResponse): { variant: ABTestVariantResult; position: number }[] {
  if (session.ranking && session.ranking.length > 0) {
    return session.ranking.map((entry) => {
      const variant = session.variants.find((v) => v.variantId === entry.variantId);
      return {
        variant: variant ?? {
          variantId: entry.variantId,
          model: entry.model,
          temperature: entry.temperature,
          snapshotId: '',
          status: 'completed' as const,
          summary: { overallScore: entry.overallScore, averages: {} },
          averageLatencyMs: entry.averageLatencyMs,
          creditCost: entry.creditCost,
        },
        position: entry.position,
      };
    });
  }

  const sorted = [...session.variants]
    .filter((v) => v.summary)
    .sort((a, b) => (b.summary?.overallScore ?? 0) - (a.summary?.overallScore ?? 0));

  return sorted.map((variant, idx) => ({ variant, position: idx + 1 }));
}

// ─── Component ─────────────────────────────────────

export function ABTestResultsTable({ session, onApply, onClose, isApplying }: ABTestResultsTableProps) {
  const rows = getSortedVariants(session);

  const thStyle: React.CSSProperties = {
    padding: '8px 10px',
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: '#64748b',
    borderBottom: '2px solid #e2e8f0',
    textAlign: 'left',
    whiteSpace: 'nowrap',
  };

  const tdStyle: React.CSSProperties = {
    padding: '10px 10px',
    fontSize: 13,
    color: '#334155',
    borderBottom: '1px solid #f1f5f9',
    whiteSpace: 'nowrap',
  };

  const metricBadgeStyle = (value: number | undefined): React.CSSProperties => ({
    ...scoreColor(value),
    padding: '2px 8px',
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
    display: 'inline-block',
    minWidth: 42,
    textAlign: 'center',
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>#</th>
              <th style={thStyle}>Modelo</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Temp.</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Score</th>
              {METRIC_KEYS.map((m) => (
                <th key={m.key} style={{ ...thStyle, textAlign: 'center' }}>{m.label}</th>
              ))}
              <th style={{ ...thStyle, textAlign: 'center' }}>Latência</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Custo</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Ação</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ variant, position }) => {
              const score = variant.summary?.overallScore;
              const isFirst = position === 1;

              return (
                <tr
                  key={variant.variantId}
                  style={{ backgroundColor: rowBackground(position) }}
                >
                  <td style={tdStyle}>
                    <span style={positionBadgeStyle(position)}>{position}</span>
                  </td>
                  <td style={{ ...tdStyle, fontWeight: 500 }}>{variant.model}</td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>{variant.temperature}</td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <span style={metricBadgeStyle(score)}>
                      {formatScore(score)}
                    </span>
                  </td>
                  {METRIC_KEYS.map((m) => {
                    const val = getMetric(variant.summary?.averages, m.key, 'altKey' in m ? m.altKey : undefined);
                    return (
                      <td key={m.key} style={{ ...tdStyle, textAlign: 'center' }}>
                        <span style={metricBadgeStyle(val)}>
                          {formatScore(val)}
                        </span>
                      </td>
                    );
                  })}
                  <td style={{ ...tdStyle, textAlign: 'center', color: '#64748b', fontSize: 12 }}>
                    {formatLatency(variant.averageLatencyMs)}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center', color: '#64748b', fontSize: 12 }}>
                    {formatCost(variant.creditCost)}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <button
                      onClick={() => onApply(variant.variantId)}
                      disabled={isApplying}
                      style={{
                        padding: '5px 14px',
                        borderRadius: 6,
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: isApplying ? 'not-allowed' : 'pointer',
                        border: isFirst ? 'none' : '1.5px solid #8b5cf6',
                        backgroundColor: isFirst ? '#8b5cf6' : '#ffffff',
                        color: isFirst ? '#ffffff' : '#8b5cf6',
                        opacity: isApplying ? 0.6 : 1,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        transition: 'opacity 150ms',
                      }}
                    >
                      <FiCheck style={{ width: 14, height: 14 }} />
                      Aplicar
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        paddingTop: 12,
        borderTop: '1px solid #f1f5f9',
      }}>
        <div style={{ fontSize: 13, color: '#64748b', flex: 1 }}>
          {session.insight ?? ''}
        </div>
        <button
          onClick={onClose}
          style={{
            padding: '7px 20px',
            borderRadius: 6,
            fontSize: 13,
            fontWeight: 500,
            cursor: 'pointer',
            border: '1px solid #e2e8f0',
            backgroundColor: '#ffffff',
            color: '#334155',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            transition: 'background-color 150ms',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f8fafc'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#ffffff'; }}
        >
          <FiX style={{ width: 14, height: 14 }} />
          Fechar
        </button>
      </div>
    </div>
  );
}
